import { createContext, createElement, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { demoIfLoaded } from "../demo/domain/load";
import { requestRepaint } from "../demo/lib/repaint";

export type Theme = "dark" | "light";
const KEY = "slt.theme";

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "dark" || v === "light") return v;
  } catch {
    /* storage unavailable */
  }
  return "dark";
}

/**
 * Dark for the ops room at three in the morning; light for a bright office
 * and a printed hand-over. The state colours do not change between them.
 *
 * ONE switch drives both stylesheets. The application's tokens key on
 * `data-theme` on <html>; the ported demo's key on `lightview` on <body>. The
 * two palettes are the same values, so having two switches for them only ever
 * produced the state where half the screen had changed and half had not.
 *
 * The demo's own `setLightView` is used for the second half rather than a class
 * toggle, because it does more than paint: it turns the night raster off (the
 * dark aerodrome scan sits on a light page like a hole), saves the choice with
 * the rest of the map view, and records the change against the session.
 */
export interface ThemeApi {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<ThemeApi | null>(null);

/**
 * One theme state for the whole shell. The header button and the Settings
 * screen both change it, and with each holding its own copy the other showed
 * the old value until a reload — so the state lives here once and is handed
 * down.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const api = useThemeState();
  return createElement(Ctx.Provider, { value: api }, children);
}

/** The theme, from the provider when there is one; on its own otherwise. */
export function useTheme(): ThemeApi {
  const ctx = useContext(Ctx);
  const own = useThemeState(ctx !== null);
  return ctx ?? own;
}

function useThemeState(inert = false): ThemeApi {
  const [theme, setTheme] = useState<Theme>(read);
  useEffect(() => {
    /* a second, unused copy must not fight the provider over the document */
    if (inert) return;
    document.documentElement.dataset.theme = theme;
    /*
     * The class is what the stylesheets key on, so it is set here directly.
     * The demo's own setLightView does more — it turns the night raster off and
     * redraws — but only matters once the domain is loaded, and asking for it
     * here would pull the whole slice into the first page. A domain that
     * arrives later reads the class on its way in.
     */
    document.body.classList.toggle("lightview", theme === "light");
    demoIfLoaded()?.setLightView(theme === "light");
    /* the panels restyle themselves; the canvas figures have to be told */
    requestRepaint();
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* storage unavailable */
    }
  }, [inert, theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return { theme, toggle, setTheme };
}
